/**
 * Meta's JavaScript SDK and the WhatsApp Embedded Signup pop-up (KTD1).
 *
 * `loadMetaSdk` adds Meta's script to the page the first time it is called,
 * which the settings card does only when the realtor presses Connect.
 * `runEmbeddedSignup` opens the pop-up for the WhatsApp Business app
 * onboarding and resolves with the one-time code and the WhatsApp Business
 * Account id the backend needs, or rejects with an `EmbeddedSignupError`
 * naming why.
 */

export const META_SDK_URL = "https://connect.facebook.net/en_US/sdk.js";

/** The Graph API version the backend pins (`GRAPH_API_VERSION`). */
export const META_GRAPH_VERSION = "v26.0";

/**
 * The only origins a session event is read from. Compared for exact
 * equality: a look-alike such as `https://www.facebook.com.example` or plain
 * `http://` is not Meta.
 */
export const META_ALLOWED_ORIGINS: readonly string[] = [
	"https://www.facebook.com",
	"https://web.facebook.com",
];

/** How long Meta's script has to load before the attempt fails. */
export const SDK_LOAD_TIMEOUT_MS = 20_000;
/** How long the realtor has to finish in Meta's window. */
export const SIGNUP_TIMEOUT_MS = 10 * 60_000;
/**
 * Meta hands back the code (to the login callback) and the account id (in a
 * session event) separately. Once one arrives, the other has this long. The
 * code itself lives 30 seconds.
 */
export const FINISH_GRACE_MS = 10_000;
/**
 * When `FB.login` returns without opening a window, how long its callback
 * has before the attempt fails as `no_window`.
 */
export const NO_WINDOW_GRACE_MS = 2_000;

export interface MetaSignupConfig {
	appId: string;
	configId: string;
}

/**
 * The Meta app id and Embedded Signup configuration id this build carries,
 * or null when either is missing (the card is then not shown).
 */
export function metaSignupConfig(): MetaSignupConfig | null {
	const appId = import.meta.env.VITE_META_APP_ID;
	const configId = import.meta.env.VITE_META_EMBEDDED_SIGNUP_CONFIG_ID;
	return appId && configId ? { appId, configId } : null;
}

export type EmbeddedSignupFailure =
	/** The realtor closed Meta's window without finishing. */
	| "cancelled"
	/** Meta's script did not load, or this build has no Meta config. */
	| "sdk_load_failed"
	/** The browser refused to open Meta's window. */
	| "popup_blocked"
	/**
	 * `FB.login` returned without opening a window and did not call back
	 * within `NO_WINDOW_GRACE_MS`.
	 */
	| "no_window"
	/** The page is not served over https, where Meta refuses `FB.login`. */
	| "insecure_page"
	/** Meta's window did not finish in time. */
	| "timeout"
	/** Meta reported an error inside its window. */
	| "meta_error";

export class EmbeddedSignupError extends Error {
	readonly reason: EmbeddedSignupFailure;

	constructor(reason: EmbeddedSignupFailure) {
		super(`Embedded Signup failed: ${reason}`);
		this.name = "EmbeddedSignupError";
		this.reason = reason;
	}
}

export interface EmbeddedSignupResult {
	code: string;
	wabaId: string;
}

interface FacebookLoginResponse {
	authResponse?: { code?: string } | null;
}

export interface FacebookSdk {
	init(options: {
		appId: string;
		autoLogAppEvents?: boolean;
		xfbml?: boolean;
		version: string;
	}): void;
	login(
		callback: (response: FacebookLoginResponse) => void,
		options: Record<string, unknown>,
	): void;
}

declare global {
	interface Window {
		FB?: FacebookSdk;
		fbAsyncInit?: () => void;
	}
}

let sdkPromise: Promise<FacebookSdk> | null = null;

/** Forgets a loaded SDK, so the next `loadMetaSdk` adds the script again. */
export function resetMetaSdkLoader(): void {
	sdkPromise = null;
}

/**
 * Adds Meta's SDK script once and initialises it with `appId`. Later calls
 * share the same promise. A failed or timed-out load removes its script and
 * is forgotten, so pressing Connect again tries again.
 */
export function loadMetaSdk(
	appId: string,
	{ timeoutMs = SDK_LOAD_TIMEOUT_MS }: { timeoutMs?: number } = {},
): Promise<FacebookSdk> {
	if (sdkPromise) return sdkPromise;
	const script = document.createElement("script");
	const loading = new Promise<FacebookSdk>((resolve, reject) => {
		const fail = () => {
			window.clearTimeout(timer);
			script.remove();
			reject(new EmbeddedSignupError("sdk_load_failed"));
		};
		const timer = window.setTimeout(fail, timeoutMs);
		window.fbAsyncInit = () => {
			const fb = window.FB;
			if (!fb) {
				fail();
				return;
			}
			fb.init({
				appId,
				autoLogAppEvents: true,
				xfbml: false,
				version: META_GRAPH_VERSION,
			});
			window.clearTimeout(timer);
			resolve(fb);
		};
		script.src = META_SDK_URL;
		script.async = true;
		script.defer = true;
		script.crossOrigin = "anonymous";
		script.onerror = fail;
		document.body.appendChild(script);
	});
	sdkPromise = loading;
	loading.catch(() => {
		if (sdkPromise === loading) sdkPromise = null;
	});
	return loading;
}

interface SessionMessage {
	event: string;
	wabaId: string | null;
	errorMessage: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

/** A `WA_EMBEDDED_SIGNUP` session event's fields, or null for anything else. */
function sessionMessage(data: unknown): SessionMessage | null {
	let value = data;
	if (typeof value === "string") {
		try {
			value = JSON.parse(value);
		} catch {
			return null;
		}
	}
	if (
		!isRecord(value) ||
		value.type !== "WA_EMBEDDED_SIGNUP" ||
		typeof value.event !== "string"
	) {
		return null;
	}
	const payload = isRecord(value.data) ? value.data : {};
	const wabaId = payload.waba_id;
	return {
		event: value.event,
		wabaId:
			typeof wabaId === "string" && /^\d{1,64}$/.test(wabaId) ? wabaId : null,
		errorMessage:
			typeof payload.error_message === "string" ? payload.error_message : null,
	};
}

export interface EmbeddedSignupOptions {
	/** Defaults to `metaSignupConfig()`. */
	config?: MetaSignupConfig | null;
	/** Defaults to `loadMetaSdk`; tests pass a fake SDK. */
	loadSdk?: (appId: string) => Promise<FacebookSdk>;
	/** The window whose session events and `open` calls are watched. */
	target?: Window;
	timeoutMs?: number;
	finishGraceMs?: number;
	noWindowGraceMs?: number;
	/** Whether the page is served over https. Defaults to reading `location`. */
	isSecure?: () => boolean;
}

const pageIsHttps = (): boolean => window.location.protocol === "https:";

/**
 * Loads the SDK and opens Embedded Signup for the WhatsApp Business app
 * onboarding. Resolves once both the code (from the login callback) and the
 * account id (from a session event whose origin is in
 * `META_ALLOWED_ORIGINS`) have arrived.
 *
 * Refuses at once, before loading anything, on a page not served over
 * https. A blocked window is recognised when the SDK's `window.open` call,
 * made while `FB.login` runs, returns null. When `FB.login` makes no
 * `window.open` call and its callback has not come within
 * `noWindowGraceMs`, the attempt fails as "no_window". Any other callback
 * without a code reads as "cancelled".
 */
export async function runEmbeddedSignup({
	config = metaSignupConfig(),
	loadSdk = loadMetaSdk,
	target = window,
	timeoutMs = SIGNUP_TIMEOUT_MS,
	finishGraceMs = FINISH_GRACE_MS,
	noWindowGraceMs = NO_WINDOW_GRACE_MS,
	isSecure = pageIsHttps,
}: EmbeddedSignupOptions = {}): Promise<EmbeddedSignupResult> {
	if (!config) throw new EmbeddedSignupError("sdk_load_failed");
	if (!isSecure()) throw new EmbeddedSignupError("insecure_page");
	let fb: FacebookSdk;
	try {
		fb = await loadSdk(config.appId);
	} catch {
		throw new EmbeddedSignupError("sdk_load_failed");
	}

	return new Promise<EmbeddedSignupResult>((resolve, reject) => {
		let code: string | null = null;
		let wabaId: string | null = null;
		let settled = false;
		let graceTimer: number | undefined;
		let noWindowTimer: number | undefined;
		// undefined: the SDK made no `window.open` call while FB.login ran.
		let opened: Window | null | undefined;

		const finish = (outcome: EmbeddedSignupResult | EmbeddedSignupError) => {
			if (settled) return;
			settled = true;
			target.removeEventListener("message", onMessage);
			target.clearTimeout(overallTimer);
			if (graceTimer !== undefined) target.clearTimeout(graceTimer);
			if (noWindowTimer !== undefined) target.clearTimeout(noWindowTimer);
			if (outcome instanceof EmbeddedSignupError) reject(outcome);
			else resolve(outcome);
		};

		const progress = () => {
			if (code && wabaId) {
				finish({ code, wabaId });
				return;
			}
			if (graceTimer === undefined) {
				graceTimer = target.setTimeout(
					() => finish(new EmbeddedSignupError("timeout")),
					finishGraceMs,
				);
			}
		};

		function onMessage(event: MessageEvent): void {
			if (!META_ALLOWED_ORIGINS.includes(event.origin)) return;
			const message = sessionMessage(event.data);
			if (!message) return;
			if (message.event.startsWith("FINISH") && message.wabaId) {
				wabaId = message.wabaId;
				progress();
			} else if (message.event === "CANCEL" && message.errorMessage) {
				finish(new EmbeddedSignupError("meta_error"));
			}
		}

		const overallTimer = target.setTimeout(
			() => finish(new EmbeddedSignupError("timeout")),
			timeoutMs,
		);
		target.addEventListener("message", onMessage);

		const originalOpen = target.open;
		target.open = ((...args: Parameters<Window["open"]>) => {
			const popup = originalOpen.apply(target, args);
			opened = popup;
			return popup;
		}) as Window["open"];
		try {
			fb.login(
				(response) => {
					const returned = response?.authResponse?.code;
					if (!returned) {
						finish(
							new EmbeddedSignupError(
								opened === null ? "popup_blocked" : "cancelled",
							),
						);
						return;
					}
					code = returned;
					progress();
				},
				{
					config_id: config.configId,
					response_type: "code",
					override_default_response_type: true,
					extras: {
						setup: {},
						featureType: "whatsapp_business_app_onboarding",
						sessionInfoVersion: "3",
					},
				},
			);
		} catch {
			finish(new EmbeddedSignupError("meta_error"));
		} finally {
			target.open = originalOpen;
		}
		if (opened === null) {
			finish(new EmbeddedSignupError("popup_blocked"));
		} else if (opened === undefined && !settled && code === null) {
			noWindowTimer = target.setTimeout(() => {
				if (code === null) finish(new EmbeddedSignupError("no_window"));
			}, noWindowGraceMs);
		}
	});
}
